import { useEffect, useRef, useState } from "react";
import { getvideoGallery } from "../services/videoGallery.service";

export const useObsessionsinAction = () => {
  const sliderRef = useRef(null);
  const videoRefs = useRef([]);
  const reelVideoRefs = useRef([]);
  const [galleries, setGalleries] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(null);

  // SAFE PLAY FUNCTION (Fix AbortError)
  const safePlay = async (video) => {
    if (!video) return;
    try {
      if (video.paused) {
        await video.play();
      }
    } catch (err) {
      // Ignore AbortError
      console.log("Play interrupted:", err?.name);
    }
  };

  // Fetch API data
  useEffect(() => {
    const fetchData = async () => {
      const data = await getvideoGallery();
      setGalleries(data);
    };
    fetchData();
  }, []);

  const loopList = [...galleries, ...galleries];

  // Infinite scroll
  const handleInfiniteScroll = () => {
    const slider = sliderRef.current;
    if (!slider) return;
    const halfWidth = slider.scrollWidth / 2;
    if (slider.scrollLeft >= halfWidth) {
      slider.scrollTo({ left: 1, behavior: "instant" });
    }

    if (slider.scrollLeft <= 0) {
      slider.scrollTo({ left: halfWidth - 1, behavior: "instant" });
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    slider.addEventListener("scroll", handleInfiniteScroll);
    return () => slider.removeEventListener("scroll", handleInfiniteScroll);
  }, []);

  // Scroll buttons
  const scroll = (direction) => {
    if (!sliderRef.current) return;
    const scrollAmount = 320;
    sliderRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });

    // Stop all videos safely
    videoRefs.current.forEach((video) => {
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });
    setPlayingIndex(null);
  };

  // PLAY VIDEO
  const handlePlay = async (index, isReel = false) => {
    const refs = isReel ? reelVideoRefs.current : videoRefs.current;
    const currentVideo = refs[index];
    if (!currentVideo) return;
    // Stop other videos
    refs.forEach((v, i) => {
      if (i !== index && v && !v.paused) {
        v.pause();
        v.currentTime = 0;
      }
    });
    setPlayingIndex(index);
    await safePlay(currentVideo);
  };

  //  TOGGLE PLAY / PAUSE
  const togglePause = async (index, isReel = false) => {
    const refs = isReel ? reelVideoRefs.current : videoRefs.current;
    const video = refs[index];
    if (!video) return;
    if (video.paused) {
      setPlayingIndex(index);
      await safePlay(video);
    } else {
      video.pause();
      setPlayingIndex(null);
    }
  };

  // Close reel
  const closeReel = () => {
    setActiveIndex(null);
    setPlayingIndex(null);
    document.body.style.overflow = "auto";
    reelVideoRefs.current.forEach((v) => {
      if (v && !v.paused) v.pause();
    });
  };

  // Mobile scroll stop fix
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    let isScrolling;
    const handleMobileScrollStop = () => {
      if (window.innerWidth > 768) return;
      clearTimeout(isScrolling);
      isScrolling = setTimeout(() => {
        videoRefs.current.forEach((video) => {
          if (video && !video.paused) {
            video.pause();
            video.currentTime = 0;
          }
        });
        setPlayingIndex(null);
      }, 150);
    };

    slider.addEventListener("scroll", handleMobileScrollStop);
    return () => {
      slider.removeEventListener("scroll", handleMobileScrollStop);
    };
  }, []);

  return {
    sliderRef,
    videoRefs,
    reelVideoRefs,
    galleries,
    loopList,
    activeIndex,
    playingIndex,
    setActiveIndex,
    scroll,
    handlePlay,
    togglePause,
    closeReel,
  };
};